import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown,
  faChevronRight,
  faLock,
  faCheckCircle,
  faBook
} from '@fortawesome/free-solid-svg-icons';

const StudyMenuItem = ({ 
  menu, 
  isExpanded, 
  onToggle, 
  onItemClick, 
  onMenuClick,
  getQuestionCount, 
  isSubscribed, 
  theme,
  onLockedItemClick,
  questionsList
}) => {
  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1rem',
      border: `1px solid ${theme?.borders?.primary}`,
      overflow: 'hidden',
      boxShadow: theme?.shadows?.base,
      transition: 'all 0.3s ease',
    },
    header: {
      padding: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
    },
    headerHover: {
      background: theme?.backgrounds?.cardHover,
    },
    iconWrapper: {
      width: '3rem',
      height: '3rem',
      borderRadius: '0.75rem',
      background: menu.icon_bg || theme?.primary?.blue?.[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      color: 'white',
      flexShrink: 0,
    },
    content: {
      flex: 1,
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '0.25rem',
    },
    description: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
    },
    arrow: {
      fontSize: '1rem',
      color: theme?.text?.tertiary,
      transition: 'all 0.3s ease',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    },
    subItemsContainer: {
      borderTop: `1px solid ${theme?.borders?.primary}`,
      background: theme?.backgrounds?.secondary,
    },
    subItem: {
      padding: '1rem 1.25rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      borderBottom: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    subItemHover: {
      background: theme?.backgrounds?.cardHover,
      paddingLeft: '1.75rem',
    },
    subItemContent: {
      flex: 1,
    },
    subItemTitle: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: theme?.text?.primary,
      marginBottom: '0.25rem',
    },
    subItemMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      fontSize: '0.75rem',
      color: theme?.text?.secondary,
    },
    questionCount: {
      background: theme?.backgrounds?.tertiary,
      padding: '0.125rem 0.5rem',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      color: theme?.text?.primary,
    },
    lockIcon: {
      color: theme?.primary?.orange?.[500],
      fontSize: '0.875rem',
    },
    progressIndicator: {
      width: '0.5rem',
      height: '0.5rem',
      borderRadius: '50%',
      background: theme?.primary?.green?.[500],
    },
    freeText: {
      color: theme?.primary?.green?.[500],
      fontSize: '0.75rem',
      fontWeight: '500',
    },
    totalText: {
      color: theme?.text?.primary,
      fontSize: '0.75rem',
      fontWeight: '500',
    },
    lockedBadge: {
      backgroundColor: theme?.primary?.yellow?.[100] || '#FEF3C7',
      padding: '0.125rem 0.5rem',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      color: theme?.text?.primary,
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      marginLeft: '0.5rem',
    },
    dotSeparator: {
      width: '4px',
      height: '4px',
      borderRadius: '50%',
      backgroundColor: theme?.text?.tertiary || '#9CA3AF',
      margin: '0 0.5rem',
    },
  };

  const handleHeaderClick = () => {
    if (menu.menu) {
      onToggle();
    } else {
      onMenuClick();
    }
  };

  // Process menu items for bookmarks
  const processedMenuItems = menu.key === "bookmark" && menu.menu
    ? [
        {
          id: 'all-bookmarks',
          domain_name: "All Bookmarks",
          requires_subscription: false,
        },
        ...menu.menu
      ]
    : menu.menu;

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
    >
      <motion.div
        style={styles.header}
        onClick={handleHeaderClick}
        whileHover={{
          backgroundColor: theme?.backgrounds?.cardHover || theme?.background?.card
        }}
      >
        <div style={styles.iconWrapper}>
          {menu.image ? (
            <img src={menu.image} alt={menu.title} style={{ width: '60%', height: '60%' }} />
          ) : (
            <FontAwesomeIcon icon={faBook} />
          )}
        </div>
        
        <div style={styles.content}>
          <h3 style={styles.title}>{menu.title}</h3>
          {menu.description && (
            <p style={styles.description}>{menu.description}</p>
          )}
        </div>
        
        {menu.menu && (
          <FontAwesomeIcon 
            icon={faChevronDown} 
            style={styles.arrow}
          />
        )}
      </motion.div>
      
      <AnimatePresence>
        {isExpanded && processedMenuItems && (
          <motion.div
            style={styles.subItemsContainer}
            
          >
            {processedMenuItems.map((item, index) => {
              const totalQuestions = getQuestionCount(item);
              const freeQuestions = item.num_questions || 0;
              const lockedQuestions = totalQuestions - freeQuestions;
              // Don't lock items in "by_topic" menu - users can access free content
              const isLocked = item.requires_subscription && !isSubscribed && menu.free_model !== 1 && menu.key !== "by_topic";
              
              // Show free/total format for non-subscribed users on "by_topic" menu
              const showFreeTotalFormat = !isSubscribed && menu.key === "by_topic";
              
              return (
                <motion.div
                  key={item.id || index}
                  style={styles.subItem}
                  whileHover={!isLocked ? {
                    backgroundColor: theme?.backgrounds?.cardHover || theme?.background?.card,
                    paddingLeft: '1.75rem'
                  } : {}}
                  onClick={() => {
                    if (isLocked) {
                      onLockedItemClick(item);
                    } else {
                      onItemClick(item);
                    }
                  }}
                  
                >
                  <div style={styles.subItemContent}>
                    <div style={styles.subItemTitle}>
                      {item.domain_name}
                    </div>
                    <div style={styles.subItemMeta}>
                      {showFreeTotalFormat ? (
                        <>
                          <span style={styles.freeText}>
                            {freeQuestions} free
                          </span>
                          <span style={styles.dotSeparator} />
                          <span style={styles.totalText}>
                            {totalQuestions} total
                          </span>
                          {lockedQuestions > 0 && (
                            <span style={styles.lockedBadge}>
                              +{lockedQuestions}
                              <FontAwesomeIcon 
                                icon={faLock} 
                                size="xs"
                                style={{ color: theme?.text?.secondary, marginLeft: '0.25rem' }}
                              />
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={styles.questionCount}>
                          {totalQuestions} Questions
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <FontAwesomeIcon 
                    icon={faChevronRight} 
                    style={{ color: theme?.text?.tertiary }}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default StudyMenuItem;